import { useState } from "react";
import { api, imageUrl, fileToBase64 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Star, Sparkles, Loader2, Camera, Footprints } from "lucide-react";
import { toast } from "sonner";
import { missingPhotoSlots, isShoes, SHOE_TIP } from "@/lib/photos";

/**
 * Photo viewer for one item: Enhanced / Original (front) / Back / Tag.
 * Lets you fix mix-ups: "Make this the front" or swap two slots; re-enhances automatically.
 */
export const ImageCompare = ({ item, onChange }) => {
  const [view, setView] = useState(item?.has_enhanced ? "enhanced" : "front");
  const [busy, setBusy] = useState(false);
  const v = item?.updated_at || "";
  const kinds = [
    { key: "enhanced", label: "Enhanced", show: item?.has_enhanced },
    { key: "front", label: "Original front", show: item?.has_front },
    { key: "back", label: "Back", show: item?.has_back },
    { key: "tag", label: "Tag", show: item?.has_tag },
    { key: "side", label: "Side", show: item?.has_side },
    { key: "sole", label: "Sole", show: item?.has_sole },
    { key: "detail", label: "Detail", show: item?.has_detail },
  ].filter((k) => k.show);
  const missing = missingPhotoSlots(item); // Task 3: optional slots still empty

  const addPhoto = async (slot, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const u = await api.addItemPhoto(item.id, slot, b64);
      toast.success(`${slot[0].toUpperCase()}${slot.slice(1)} photo added`);
      onChange?.(u);
      setView(slot);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the photo");
    } finally {
      setBusy(false);
    }
  };

  const swap = async (a, b) => {
    setBusy(true);
    try {
      const u = await api.swapImages(item.id, a, b);
      toast.success(a === "front" || b === "front" ? "Front updated — re-polishing the photo" : "Photos swapped");
      onChange?.(u);
      setView("front");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not swap");
    } finally {
      setBusy(false);
    }
  };

  const reEnhance = async () => {
    setBusy(true);
    try { const u = await api.reEnhance(item.id); toast.success("Photo re-polished"); onChange?.(u); setView("enhanced"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not re-enhance"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl bg-secondary ring-1 ring-black/5">
        <img
          src={imageUrl(item.id, view, v)}
          alt={item.name || "garment"}
          className="h-full w-full object-cover"
          data-testid="item-detail-image"
        />
        {busy && <div className="absolute inset-0 bg-background/60 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-action" /></div>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {kinds.map((k) => (
          <Button
            key={k.key}
            size="sm"
            variant={view === k.key ? "default" : "secondary"}
            onClick={() => setView(k.key)}
            data-testid={`image-toggle-${k.key}`}
            className="rounded-full"
          >
            {k.label}
          </Button>
        ))}
      </div>
      {/* complete the photo set (Task 3) */}
      {missing.length > 0 && (
        <div className="mt-2.5 rounded-xl border border-dashed border-border bg-gray-25 px-3 py-2.5" data-testid="image-missing-slots">
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Camera className="h-3 w-3" /> Incomplete photos - add the {missing.map((m) => m.label.toLowerCase()).join(" and ")}{isShoes(item) ? `. ${SHOE_TIP}` : "."}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {missing.map((m) => (
              <label key={m.key} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-border bg-background text-xs font-medium cursor-pointer hover:bg-secondary transition-colors" data-testid={`image-add-${m.key}`}>
                {isShoes(item) ? <Footprints className="h-3.5 w-3.5" /> : <Camera className="h-3.5 w-3.5" />} Add {m.label.toLowerCase()}
                <input type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => addPhoto(m.key, e.target.files?.[0])} />
              </label>
            ))}
          </div>
        </div>
      )}
      {/* fix-up tools */}
      <div className="mt-2 flex flex-wrap gap-2" data-testid="image-fix-tools">
        {(view === "back" || view === "tag") && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => swap("front", view)} className="rounded-full" data-testid="image-make-front">
            <Star className="h-3.5 w-3.5 mr-1.5" /> Make this the front
          </Button>
        )}
        {item?.has_front && item?.has_back && view !== "tag" && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => swap("front", "back")} className="rounded-full text-muted-foreground" data-testid="image-swap-front-back">
            <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" /> Swap front / back
          </Button>
        )}
        {item?.has_back && item?.has_tag && (view === "tag" || view === "back") && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => swap("back", "tag")} className="rounded-full text-muted-foreground" data-testid="image-swap-back-tag">
            <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" /> Swap back / tag
          </Button>
        )}
        {(view === "enhanced" || view === "front") && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={reEnhance} className="rounded-full text-muted-foreground" data-testid="image-re-enhance">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Re-polish
          </Button>
        )}
      </div>
    </div>
  );
};
